/**
 * Language Context
 * Manages language/translation state across the application
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../lib/axios'; // Use configured axios with CSRF
import { usePersistedState, STORAGE_KEYS, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../hooks/usePersistedState';
import TRANSLATIONS from '../i18n'; // Import full translation table

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageRaw] = usePersistedState(STORAGE_KEYS.LANGUAGE, DEFAULT_LANGUAGE);
  const [customTranslations, setCustomTranslations] = useState({});

  // Validate and set language (only allow supported languages)
  const setLanguage = (lang) => {
    const normalizedLang = lang?.toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(normalizedLang)) {
      setLanguageRaw(normalizedLang);
    }
  };

  // Ensure current language is valid
  const validLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;

  // Fetch custom translations from backend
  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const response = await axios.get(`${API}/translations/${validLanguage}`);
        if (response.data) {
          setCustomTranslations(response.data);
        }
      } catch (error) {
        // Use fallback translations
        console.log('Using fallback translations');
      }
    };
    fetchTranslations();
  }, [validLanguage]);

  // Translation function - checks custom first, then full i18n table, then English fallback
  const t = (key) => {
    return customTranslations[key] || TRANSLATIONS[validLanguage]?.[key] || TRANSLATIONS.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ 
      language: validLanguage, 
      setLanguage, 
      t, 
      translations: TRANSLATIONS[validLanguage], 
      customTranslations 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
